/**
 * The four KKF apps and how to link between them.
 *
 * KKF is one design system across four independently deployed static sites:
 *
 *   kkfmongolia.com            the Foundation      app_website
 *   garage.kkfmongolia.com     the Garage          app_garage
 *   resort.kkfmongolia.com     the resort          app_resort
 *   partners.kkfmongolia.com   the Partners        app_partners
 *
 * Each app links to the other three by absolute URL, and to its own pages by route. The
 * URLs are configuration, not content, so they come from the environment — see the env
 * table in `CLAUDE.md`. They are **not secrets**: they are public hostnames baked into
 * the bundle, which is why they carry Vite's `VITE_` prefix and ship as a committed
 * `.env.example` in every app.
 *
 * `resolveSiteLinks` takes the env record rather than reading `import.meta.env` itself.
 * That is deliberate: `import.meta` is a bundler-specific global, and this module has to
 * stay importable from React Native when `app_mobile` arrives — the same reason
 * `tokens.ts` holds only plain strings and numbers. The caller passes its own env:
 *
 *   const links = resolveSiteLinks(import.meta.env)   // Vite apps
 *   const links = resolveSiteLinks(process.env)        // anything Node-side
 */

/** The four apps, by the name used everywhere in the codebase. */
export type AppKey = 'foundation' | 'garage' | 'resort' | 'partners'

/** An absolute origin per app, with no trailing slash. */
export type SiteLinks = Record<AppKey, string>

/**
 * Which variable configures which app.
 *
 * Typed as its own `Record<AppKey, string>` rather than as `SiteLinks`: these are variable
 * *names*, not URLs, and reusing the URL type here type-checks while reading as if
 * `SITE_LINK_ENV_VARS.garage` were a link you could navigate to.
 */
export const SITE_LINK_ENV_VARS: Readonly<Record<AppKey, string>> = {
  foundation: 'VITE_FOUNDATION_URL',
  garage: 'VITE_GARAGE_URL',
  resort: 'VITE_RESORT_URL',
  partners: 'VITE_PARTNERS_URL',
}

/**
 * The production hosts, and the defaults. A build with no `.env` at all is therefore a
 * correct production build — the environment exists to point an app at localhost or a
 * Cloudflare preview, not to make production work.
 */
export const PRODUCTION_SITE_LINKS: Readonly<SiteLinks> = {
  foundation: 'https://kkfmongolia.com',
  garage: 'https://garage.kkfmongolia.com',
  resort: 'https://resort.kkfmongolia.com',
  partners: 'https://partners.kkfmongolia.com',
}

/** An env value only counts if it is a non-empty string — an unset Vite var is `''`. */
const configured = (value: unknown): value is string => typeof value === 'string' && value.trim() !== ''

/**
 * Trailing slashes are stripped so `${links.resort}/library` is always one slash, never
 * two. `//library` would resolve on most hosts but not all, and it looks like a bug in
 * every log it appears in.
 */
const origin = (value: string): string => value.trim().replace(/\/+$/, '')

/**
 * Reads the four app URLs out of an environment record, falling back to production.
 *
 * @param env any string map — `import.meta.env`, `process.env`, or a literal in a test.
 */
export function resolveSiteLinks(env: Readonly<Record<string, unknown>> = {}): SiteLinks {
  const keys = Object.keys(SITE_LINK_ENV_VARS) as AppKey[]
  return keys.reduce((links, key) => {
    const value = env[SITE_LINK_ENV_VARS[key]]
    links[key] = origin(configured(value) ? value : PRODUCTION_SITE_LINKS[key])
    return links
  }, {} as SiteLinks)
}

/**
 * A path on another app: `appUrl(links, 'resort', '/library')`.
 *
 * Prefer linking at an app's root or one of its stable top-level routes. A deep link
 * across apps is a link the other app can rename without knowing it broke this one.
 */
export function appUrl(links: SiteLinks, app: AppKey, path = '/'): string {
  return path === '/' || path === '' ? links[app] : `${links[app]}/${path.replace(/^\/+/, '')}`
}
